import React, { useState, useEffect } from 'react';
import { IconPoll, IconX, IconTrash, IconPlus } from '../Icons';

export interface PollOptionPayload {
  id: string;
  text: string;
  votes: string[];
}

export interface PollPayload {
  question: string;
  options: PollOptionPayload[];
  allowMultiple: boolean;
}

interface PollModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pollData: PollPayload) => Promise<void> | void;
}

export const PollModal: React.FC<PollModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setQuestion('');
      setOptions(['', '']);
      setAllowMultiple(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAddOption = () => {
    if (options.length >= 8) return;
    setOptions([...options, '']);
  };

  const handleOptionChange = (text: string, index: number) => {
    const next = [...options];
    next[index] = text;
    setOptions(next);
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, idx) => idx !== index));
  };

  const validOptions = options.map(o => o.trim()).filter(Boolean);
  const canSubmit = question.trim().length > 0 && validOptions.length >= 2 && !submitting;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const formattedOptions: PollOptionPayload[] = validOptions.map((text, idx) => ({
        id: `opt_${Date.now()}_${idx}`,
        text,
        votes: []
      }));

      await onSubmit({
        question: question.trim(),
        options: formattedOptions,
        allowMultiple
      });

      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px'
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '24px',
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(15, 42, 74, 0.28)',
          border: '1.5px solid rgba(226, 232, 240, 0.95)',
          overflow: 'hidden',
          animation: 'zoikDrawerSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px 16px',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 112, 243, 0.3)'
              }}
            >
              <IconPoll size={22} color="#ffffff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 900, color: '#0f172a' }}>
                Crear Encuesta
              </h3>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                Votación interactiva para el chat
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: 'none',
              background: '#f1f5f9',
              color: '#64748b',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#e2e8f0';
              e.currentTarget.style.color = '#0f172a';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div
            style={{
              padding: '20px 24px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px'
            }}
          >
            {/* Pregunta */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px' }}>
                  PREGUNTA DE LA ENCUESTA
                </label>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {question.length}/160
                </span>
              </div>
              <textarea
                rows={2}
                maxLength={160}
                placeholder="Ej. ¿A qué hora nos reunimos para revisar la propuesta?"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '12px 14px',
                  borderRadius: '14px',
                  border: '1.5px solid #cbd5e1',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#0f172a',
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#0070f3';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 112, 243, 0.15)';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Opciones */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#475569', letterSpacing: '0.6px' }}>
                  OPCIONES DE RESPUESTA ({validOptions.length}/8)
                </label>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>Mínimo 2 opciones</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {options.map((opt, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '1.5px solid #e2e8f0',
                      padding: '4px 8px 4px 10px',
                      transition: 'border-color 0.15s ease'
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        background: 'rgba(0, 112, 243, 0.12)',
                        color: '#0070f3',
                        fontSize: '11.5px',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {idx + 1}
                    </div>

                    <input
                      type="text"
                      maxLength={100}
                      placeholder={`Opción ${idx + 1}`}
                      value={opt}
                      onChange={e => handleOptionChange(e.target.value, idx)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (idx === options.length - 1 && options.length < 8) {
                            handleAddOption();
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '13.5px',
                        fontWeight: 600,
                        color: '#0f172a',
                        padding: '8px 4px',
                        fontFamily: 'inherit'
                      }}
                    />

                    {options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(idx)}
                        title="Eliminar opción"
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          border: 'none',
                          background: 'transparent',
                          color: '#ef4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#fee2e2')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <IconTrash size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {options.length < 8 && (
                <button
                  type="button"
                  onClick={handleAddOption}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    width: '100%',
                    padding: '10px',
                    borderRadius: '12px',
                    border: '1.5px dashed #0070f3',
                    background: 'rgba(0, 112, 243, 0.04)',
                    color: '#0070f3',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginTop: '8px',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(0, 112, 243, 0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(0, 112, 243, 0.04)';
                  }}
                >
                  <IconPlus size={14} color="#0070f3" />
                  Añadir otra opción ({options.length}/8)
                </button>
              )}
            </div>

            {/* Toggle Múltiples Respuestas */}
            <div
              onClick={() => setAllowMultiple(!allowMultiple)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                borderRadius: '14px',
                background: '#f8fafc',
                border: '1.5px solid #e2e8f0',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={e => (e.currentTarget.style.background = '#f8fafc')}
            >
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a' }}>
                  Permitir selección múltiple
                </div>
                <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>
                  Los participantes podrán votar por más de una opción
                </div>
              </div>

              <div
                style={{
                  width: '42px',
                  height: '24px',
                  borderRadius: '12px',
                  background: allowMultiple ? '#0070f3' : '#cbd5e1',
                  position: 'relative',
                  transition: 'background 0.2s ease'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '3px',
                    left: allowMultiple ? '21px' : '3px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    transition: 'left 0.2s ease'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#ffffff',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '10px 18px',
                borderRadius: '12px',
                border: '1.5px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontWeight: 700,
                fontSize: '13px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                padding: '10px 22px',
                borderRadius: '12px',
                border: 'none',
                background: canSubmit ? 'linear-gradient(135deg, #0070f3 0%, #0056d6 100%)' : '#cbd5e1',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '13px',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                boxShadow: canSubmit ? '0 4px 14px rgba(0, 112, 243, 0.35)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.15s ease'
              }}
            >
              {submitting ? (
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'zoikSpin 0.8s linear infinite'
                    }}
                  />
                  Publicando...
                </>
              ) : (
                <>
                  <IconPoll size={16} color="#ffffff" />
                  Publicar Encuesta
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
